import { useMemo, useState } from 'react'
import { useDataChange, useDataValue } from 'Simple/Data.js'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import { useMutation } from 'Data/Api.js'
import mutation from './mutation.graphql.js'
import { useDwt } from 'DesignSystem/DWT/useDwt'
import { useUppy } from 'DesignSystem/FileUploader/react.js'
import { downloadBlob } from 'Logic/downloadUrl.js'

export default function useDataTransform(props) {
  let uppy = useUppy(props)
  let {
    useNewDrivers,
    toggleUseNewDrivers,
    sources,
    currentSource,
    isLoading,
    isAcquiring,
    debug,
    onSourceChange,
    onImagesAcquire,
  } = useDwt({
    containerId: 'dwtControlContainerId',
    convertMultiPageToPdf: true,
    onImagesAcquired,
  })
  let parent_id = useDataValue({
    context: 'patient_documents',
    path: 'parent_id',
    viewPath: props.viewPath,
  })
  let patient_id = useDataValue({
    context: 'patient',
    path: 'id',
    viewPath: props.viewPath,
  })

  let [isUploading, setIsUploading] = useState(false)

  let setRefetch = useDataChange({
    context: 'patient_documents',
    path: 'refetch',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()
  let [, executeMutation] = useMutation(mutation)

  return useMemo(() => {
    return {
      useNewDrivers,
      toggleUseNewDrivers,
      sources: sources.map(source => ({ id: source, text: source })),
      currentSource,
      isLoading,
      isAcquiring,
      isUploading,
      debug,
      onSourceChange,
      onImagesAcquire,
    }
  }, [
    useNewDrivers,
    toggleUseNewDrivers,
    currentSource,
    isAcquiring,
    isUploading,
    isLoading,
    sources,
    debug,
    onSourceChange,
    onImagesAcquire,
  ])

  async function onImagesAcquired(images) {
    try {
      setIsUploading(true)

      uppy.cancelAll()

      for (let image of images) {
        try {
          uppy.addFile(image)
        } catch (err) {
          if (!err.isRestriction) {
            uppy.log(err)
            uppy.info(
              'There was an error when trying to add your files. Please try again.',
              'error',
              3000
            )
          }
        }
      }

      try {
        await uppy.upload()
      } catch (err) {
        // download the images for users to re-upload if needed
        await download(images)

        return notify(
          notifyError(
            'There was a problem uploading the document into our system. Please try again.'
          )
        )
      }

      let attachments = uppy.getFiles()

      let results = await Promise.allSettled(
        attachments.map(async attachment => {
          let mutationResponse = await executeMutation({
            patient_id,
            parent_id,
            name: attachment.name,
            file_uid: attachment.meta.location.substring(
              attachment.meta.location.lastIndexOf('/') + 1
            ),
          })
          if (mutationResponse.error) {
            throw mutationResponse.error
          }
        })
      )
      if (results.some(result => result?.status !== 'fulfilled')) {
        // download the images for users to re-upload if needed
        await download(images)

        return notify(
          notifyError(
            'There was a problem storing the document. Please try again.'
          )
        )
      }

      console.debug('TWAIN debug', debug.join('\n'))

      setFlowTo(normalizePath(props.viewPath, '../../No'))
      setRefetch(Date.now())
    } finally {
      setIsUploading(false)
    }
  }

  async function download(images) {
    // eslint-disable-next-line
    let results = await Promise.allSettled(
      images.map(image =>
        downloadBlob({ blob: image.data, file_name: image.name })
      )
    )
    if (results.some(result => result?.status !== 'fulfilled')) {
      notify(notifyError('There was a problem downloading the images.'))
    }
  }
}
